import React, { FC } from "react";
import {
  default as H,
  History,
  LocationDescriptorObject,
  LocationState
} from "history";
import { NavLink, NavLinkProps } from "react-router-dom";
import {
  FlatParamMap,
  parse,
  QParam,
  SelectedTenant,
  stringify,
  useQueryParamMap,
  useQueryParamString
} from "polyrouter";
import { KeepProps, Nesting, Toggleable, WithParams } from "./types";
import { AllQueryParam } from "polyrouter/allQueryParams";
import { generatePath, useRouteMatch } from "react-router";

export function forceObject<S = LocationState>(
  to: History.LocationDescriptor<S>
): LocationDescriptorObject<S> {
  if (typeof to === "string") {
    return {
      pathname: to
    };
  }
  return to;
}

type LinkProps<S> =
  | History.LocationDescriptor<S>
  | ((location: H.Location<S>) => H.LocationDescriptor<S>);

export function useKeeperParams(
  persist?: Array<AllQueryParam>
): FlatParamMap | null {
  const tenant = useQueryParamString<SelectedTenant>("tenantId");
  const tenants: Array<SelectedTenant> = tenant ? ["tenantId"] : [];
  const keepers: Array<AllQueryParam> = [...(persist || []), ...tenants];
  const [keep] = useQueryParamMap<AllQueryParam>(keepers);
  return keep;
}

export type PolyLinkOptions = {
  keepers: Record<string, QParam> | null;
  root?: string;
  params?: { [paramName: string]: string | number | boolean | undefined };
};

export function mergeParamsToLocation<S>(
  to: History.LocationDescriptor<S>,
  { keepers, root, params }: PolyLinkOptions
) {
  const newTo = forceObject(to);
  if (keepers) {
    const oldSearch = parse(newTo.search);
    const fullSearch = {
      ...oldSearch,
      ...keepers
    };
    newTo.search = stringify(fullSearch);
  }
  if (root) {
    newTo.pathname = generatePath(`${root}${newTo.pathname}`, params || {});
  }
  return newTo;
}

export function mergePersistedQueryArgs<S = LocationState>(
  to: LinkProps<S>,
  opts: PolyLinkOptions
): LinkProps<S> {
  if (to instanceof Function) {
    return (location: H.Location<S>) => {
      const l: H.LocationDescriptor = to(location);
      return mergeParamsToLocation(l, opts);
    };
  }
  return mergeParamsToLocation(to, opts);
}

const PNavLink: FC<NavLinkProps &
  KeepProps &
  Nesting &
  Toggleable &
  WithParams> = ({ persist, nest, disabled, to, params, ...props }) => {
  const { path, params: rootParams } = useRouteMatch();
  const keepers = useKeeperParams(persist);
  const root = nest ? path : undefined;
  const newTo = mergePersistedQueryArgs(to, {
    keepers,
    root,
    params: {
      ...rootParams,
      ...params
    }
  });
  if (disabled) {
    return <span className="nav-link-disabled">{props.children}</span>;
  }
  return <NavLink {...props} to={newTo} />;
};
export default PNavLink;
